import Image from 'next/image';
import { memo } from 'react';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { WorkspaceAvatar, WorkspaceAvatarAndName } from '~/components/WorkspaceAvatarAndName';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { usePublicCustomLogo } from '~/hooks/usePublicCustomLogo';

export const PublicWorkspaceInfo = memo(() => {
  const { workspaceImage, workspaceName } = usePublicWorkspace();
  const { customLogoUrl } = usePublicCustomLogo();
  const { headerForegroundColor } = usePublicCustomColors();

  if (customLogoUrl) {
    // Custom brand marks are frequently wide (horizontal wordmarks), so the logo renders whole at
    // the avatar slot's height with auto width instead of going through WorkspaceAvatar's square
    // crop. Rendered locally rather than changing the shared WorkspaceAvatar, which private
    // surfaces use as a square avatar. max-w bracket is a proto-fidelity cap
    // (mattq/proto-presentation-mode) — no configured max-w steps exist near it. `unoptimized`
    // skips the next/image resizer, whose fixed-size renditions would soften wide marks scaled by
    // CSS; height/width are only the intrinsic-ratio hint.
    return (
      <Image
        alt={`Logo for ${workspaceName}`}
        className="h-8 w-auto max-w-[160px] shrink-0 object-contain object-left"
        data-testid="PUBLIC_CUSTOM_LOGO"
        height={32}
        src={customLogoUrl}
        unoptimized
        width={192}
      />
    );
  }

  // When the share link sets a custom header color, render the name with the derived header
  // foreground color so it stays readable against the recolored header. Composed here (avatar + name)
  // rather than threading a color prop into the shared WorkspaceAvatarAndName, which is also used by
  // private/in-app surfaces that must render unchanged.
  if (headerForegroundColor) {
    return (
      <div className="flex items-center" data-testid="WORKSPACE_AVATAR_AND_NAME">
        <WorkspaceAvatar image={workspaceImage} name={workspaceName} />
        <div
          className="ml-3 truncate font-medium ads:text-secondary text-16 text-gray-10"
          data-testid="WORKSPACE_NAME"
          style={{ color: headerForegroundColor }}
        >
          {workspaceName}
        </div>
      </div>
    );
  }

  return <WorkspaceAvatarAndName image={workspaceImage} name={workspaceName} />;
});

PublicWorkspaceInfo.displayName = 'PublicWorkspaceInfo';
